import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../../../../../../components/ui/card";
import { Button } from "../../../../../../components/ui/button";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../../components/ui/table";
import { Download } from 'lucide-react';

import { getUnitFromSettings, getUnitLabel } from "../../../../../../utils/unitUtils";
import { Signature } from './Signature';
import { AntdDate } from '../../../../../../components/ui/antd-date';
import { MultiSelect } from '../../../../../../components/ui/multi-select';

// Updated interface to match JSON structure
interface AlignmentData {
  id: number;
  location: string;
  "0deg": number;
  "90deg": number;
  note?: string;
}

interface ProjectDetails {
  client: string;
  buckleArrestorAssemblyNo: string;
  wpsNo: string;
  jointType: string;
  date: string;
  project: string;
  procedure: string;
  weldingPosition: string;
  drawingNumber: string;
  pipeSize: string;
  notes: string;
}

interface AlignmentProps {
  selectedItem: any;
  selectedPipe: any;
  materials: any;
  specifications: any;
  alignmentWeldData: any;
  alignmentState: any;
  onAlignmentStateChange: (state: any) => void;
  customerRepresentativeList: Array<{ value: string, label: string }>;
  qcList: Array<{ value: string, label: string }>;
  onDownloadReport?: () => void;
  downloadLoading?: boolean;
  isEditMode?: boolean;
  canEdit?: boolean;
  canReport?: boolean;
  canSign?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isAdminOrSuperAdmin?: boolean;
}

export const Alignment: React.FC<AlignmentProps> = ({
  selectedItem,
  selectedPipe,
  materials,
  specifications,
  alignmentWeldData,
  alignmentState,
  onAlignmentStateChange,
  customerRepresentativeList,
  qcList,
  onDownloadReport,
  downloadLoading = false,
  isEditMode = false,
  canEdit = true,
  canReport = true,
  canSign = true,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false
}) => {
  const [unit, setUnit] = useState<string>('Inches');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState<string>('');

  const [readingsNotes, setReadingsNotes] = useState<string>('');
  const [verificationStatementNotes, setVerificationStatementNotes] = useState<string>('THE UNDERSIGNED CERTIFIES THAT THE DIMENSIONS ON THE CORRESPONDING PARTS/PRODUCTS SPECIFIED ABOVE ARE IN ACCORDANCE WITH ALL APPLICABLE DRAWING TOLERANCES AND CONTRACT REQUIREMENTS.'); 

  // // Load unit from settings
  // useEffect(() => {
  //   const loadUnit = () => {
  //     const unitFromSettings = getUnitFromSettings();
  //     setUnit(unitFromSettings);
  //   };
  //   loadUnit();
  // }, []);

  // Sync local state with parent state when alignmentState changes
  useEffect(() => {

   // console.log('alignmentWeldData', alignmentWeldData);
   // console.log('alignmentState', alignmentState);
    if (alignmentWeldData) {
      
      // // Initialize details if it doesn't exist
      // if (!alignmentWeldData.details) {
      //   alignmentWeldData.details = {
      //     inspectionProcedureIds: [],
      //     acceptanceCriteriaIds: [],
      //     weldProcessIds: [],
      //     jointTypeIds: [],
      //     note: '',
      //     readingsNote: '',
      //     verificationStatementNote: '',
      //     weldPasses: [],
      //     lengthEndToEnd: ''
      //   }
      // }

      // Restore saved values
      setVerificationStatementNotes(alignmentWeldData?.verificationStatement || 'THE UNDERSIGNED CERTIFIES THAT THE DIMENSIONS ON THE CORRESPONDING PARTS/PRODUCTS SPECIFIED ABOVE ARE IN ACCORDANCE WITH ALL APPLICABLE DRAWING TOLERANCES AND CONTRACT REQUIREMENTS.');
      setReadingsNotes(alignmentWeldData?.details?.readingsNote || ''); 
      setNotes(alignmentWeldData?.details?.note || '');
      setDate(alignmentWeldData?.date || new Date().toISOString().split('T')[0]);

      setCustomerSignature(alignmentWeldData?.customerRepresentativeId?.toString() || null);
      setQcSignature(alignmentWeldData?.qcInspectorId?.toString() || null);
      setDetailedOverallLength(alignmentWeldData?.details?.lengthEndToEnd || null);

      // Restore selected specification IDs
      setSelectedInspectionProcedureIds(alignmentWeldData?.details?.inspectionProcedureIds || []);
      setSelectedWeldPositionIds(alignmentWeldData?.details?.weldPositionIds || []);
      setSelectedJointTypeIds(alignmentWeldData?.details?.jointTypeIds || []);
      if(!alignmentWeldData?.details?.date){
        setDate(new Date().toISOString().split('T')[0]);
        if (alignmentWeldData) {
          alignmentWeldData.date = new Date().toISOString().split('T')[0];
        }
      }

     
    }
  }, [alignmentWeldData]);

  // Update alignment readings when alignmentWeldData changes (separate effect for readings)
  useEffect(() => {
    if (alignmentWeldData?.alignmentReadings && alignmentWeldData.alignmentReadings.length > 0) {
      setDynamicAlignmentData(alignmentWeldData.alignmentReadings);
    }
  }, [alignmentWeldData?.alignmentReadings]);

  // Helper function to get display value for input fields (convert from inches to display unit)
  const getDisplayValue = (value: number | null): number => {
    if (typeof value !== 'number' || isNaN(value) || value === null) return 0;

    // If the unit is already inches, no conversion needed
    if (unit === 'Inches') {
      return value;
    }

    // If the unit is meters (mm), convert from inches to mm
    if (unit === 'meters') {
      const mmValue = value * 25.4;
      const roundedValue = Math.round(mmValue * 1000) / 1000;
      return roundedValue;
    }

    return value;
  };

  // Helper function to convert input value back to inches for storage
  const convertInputToInches = (value: number | null): number => {
    if (typeof value !== 'number' || isNaN(value) || value === null) return 0;

    // If the unit is already inches, no conversion needed
    if (unit === 'Inches') {
      return value;
    }

    // If the unit is meters (mm), convert from mm to inches
    if (unit === 'meters') {
      const inchesValue = value / 25.4;
      const roundedValue = Math.round(inchesValue * 1000000) / 1000000;
      return roundedValue;
    }

    return value;
  };


  const [detailedOverallLength, setDetailedOverallLength] = useState<number | null>(null);



  const [ptlName, setPtlName] = useState<string>('');
  const [ptlSignature, setPtlSignature] = useState<string>('');
  const [ptlDate, setPtlDate] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companySignature, setCompanySignature] = useState<string>('');
  const [companyDate, setCompanyDate] = useState<string>('');

  const [customerSignature, setCustomerSignature] = useState<string | null>(null);
  const [qcSignature, setQcSignature] = useState<string | null>(null);

  // Missing state variables for form controls
  const [selectedInspectionProcedureIds, setSelectedInspectionProcedureIds] = useState<string[]>([]);
  const [selectedWeldPositionIds, setSelectedWeldPositionIds] = useState<string[]>([]);
  const [selectedJointTypeIds, setSelectedJointTypeIds] = useState<string[]>([]);
  const [selectedDegrees, setSelectedDegrees] = useState<string[]>([]);
  const [selectedAlignmentLocations, setSelectedAlignmentLocations] = useState<string[]>([]);

  // Create options for specifications dropdowns
  const alignmentOptions = specifications
    .filter((spec: any) => spec.type === 'ALIGNMENT')
    .map((alignment: any) => {
      const files = alignment.files?.[0]
      return {
        value: alignment.id,
        alignmentLocation: files?.details?.alignment?.alignmentLocation,
        degrees: files?.details?.alignment?.degrees,
        note: files?.details?.alignment?.note,
        numberOfAxialAlignments: files?.details?.alignment?.numberOfAxialAlignments,
        label: files?.fileName || `Alignment ${alignment.id}`
      }

    });

    useEffect(() => {
      if (alignmentOptions.length > 0) {
        const selectedAlignment = alignmentOptions[0]
        
      
        
        // Always set the degrees and locations from the specification
        setSelectedDegrees(selectedAlignment.degrees || []);
        setSelectedAlignmentLocations(selectedAlignment.alignmentLocation || []);
        
       
        
        // If we have existing alignment data, use it
        if(alignmentWeldData?.alignmentReadings && alignmentWeldData.alignmentReadings.length > 0){
          setDynamicAlignmentData(alignmentWeldData.alignmentReadings);
        }
      
      
      } else {
        console.log('No alignment options found. Specifications:', specifications);
      }
    }, [specifications]);
    


  // Create dynamic alignment data structure
  const [dynamicAlignmentData, setDynamicAlignmentData] = useState<Array<{
    id: number;
    location: string;
    readings: { [key: string]: number };
    note?: string;
  }>>([]);

  // Update dynamic alignment data when specification changes or alignmentWeldData changes
  useEffect(() => {
  
    // Priority 1: Use alignmentWeldData if it exists and has data
    if (alignmentWeldData?.alignmentReadings && alignmentWeldData.alignmentReadings.length > 0) {
      setDynamicAlignmentData(alignmentWeldData.alignmentReadings);
      return;
    }

    // Priority 2: Create new data structure while preserving existing values
    if (selectedDegrees.length > 0 && selectedAlignmentLocations.length > 0) {
      const newData = selectedAlignmentLocations.map((location: any, index: number) => {
        const readings: { [key: string]: number } = {};
        
        // Try to preserve existing values from alignmentWeldData first, then from current dynamicAlignmentData
        const existingRowFromData = alignmentWeldData?.alignmentReadings?.find((row: any) => row.location === location);
        const existingRow = existingRowFromData || dynamicAlignmentData.find((row: any) => row.location === location);
        
        selectedDegrees.forEach((degree: any) => {
          const degreeKey = `${degree}deg`;
          // Preserve existing value if available, otherwise use 0
          readings[degreeKey] = existingRow?.readings?.[degreeKey] || 0;
        });

        return {
          id: index + 1,
          location,
          readings,
          note: existingRow?.note || ''
        };
      });

      setDynamicAlignmentData(newData);
    } else {
      console.log('Not enough data to create dynamic alignment data');
    }
  }, [selectedDegrees, selectedAlignmentLocations, alignmentWeldData]);

  // Additional effect to handle initial data setup when no alignmentWeldData exists
  useEffect(() => {
    // Only create default data if we don't have any data and have the required selections
    if (!alignmentWeldData?.alignmentReadings?.length && 
        selectedDegrees.length > 0 && 
        selectedAlignmentLocations.length > 0 && 
        dynamicAlignmentData.length === 0) {
      
      const defaultData = selectedAlignmentLocations.map((location: any, index: number) => {
        const readings: { [key: string]: number } = {};
        selectedDegrees.forEach((degree: any) => {
          const degreeKey = `${degree}deg`;
          readings[degreeKey] = 0;
        });

        return {
          id: index + 1,
          location,
          readings,
          note: ''
        };
      });

      setDynamicAlignmentData(defaultData);
    }
  }, [selectedDegrees, selectedAlignmentLocations, alignmentWeldData, dynamicAlignmentData.length]);





    // Create options for specifications dropdowns
    const inspectionProcedureOptions = specifications
    .filter((spec: any) => spec.type === 'INSPECTION_PROCEDURE')
    .map((inspectionProcedure: any) => {
      const files= inspectionProcedure.files?.[inspectionProcedure.files.length - 1]
      return {
      value: inspectionProcedure.id,
      standardName: files?.details?.inspectionProcedure?.standard,
      label: files?.fileName || `Inspection Procedure ${inspectionProcedure.id}`
      }
    
    });

  // Create options for specifications dropdowns
  const weldPositionOptions = specifications
    .filter((spec: any) => spec.type === 'WELD_PROCESS')
    .flatMap((weldProcess: any,index: number) => {
      if(index === 0){
      // Try to get processType from different possible locations
      let weldPosition = null;
      let fileName = null;
      // First try: files[0].details.weldProcess.processType (new structure)
      if (weldProcess.files?.[0]?.details?.weldProcess?.weldPosition) {
        weldPosition = weldProcess.files[0].details.weldProcess.weldPosition;
        fileName = weldProcess.files[0].fileName;
      }
      // Second try: details.weldProcess.processType (mock data structure)
      else if (weldProcess.details?.weldProcess?.weldPosition) {
        weldPosition = weldProcess.details.weldProcess.weldPosition;
        fileName = weldProcess.fileName;
      }

      // Handle both array and string cases
      if (Array.isArray(weldPosition)) {
        return weldPosition.map((process: any, index: number) => ({
          value:process,
          label: process
        }));
      } else if (typeof weldPosition === 'string') {
        // If processType is a single string, convert it to an array with one item
        return [{
          value: 1,
          label: weldPosition
        }];
      }

      // If no processType found, try to use the fileName as a fallback
      if (fileName) {
        return [{
          value: 1,
          label: fileName
        }];
      }
    }
      return [];
    });



  // Create options for specifications dropdowns
  const jointTypeOptions = specifications
    .filter((spec: any) => spec.type === 'WELD_PROCESS')
    .flatMap((weldProcess: any,index: number) => {
      if(index === 0){
      // Try to get processType from different possible locations
      let jointType = null;
      let fileName = null;

      // First try: files[0].details.weldProcess.processType (new structure)
      if (weldProcess.files?.[0]?.details?.weldProcess?.jointType) {
        jointType = weldProcess.files[0].details.weldProcess.jointType;
        fileName = weldProcess.files[0].fileName;
      }
      // Second try: details.weldProcess.jointType (mock data structure)
      else if (weldProcess.details?.weldProcess?.jointType) {
        jointType = weldProcess.details.weldProcess.jointType;
        fileName = weldProcess.fileName;
      }


      // Handle both array and string cases
      if (Array.isArray(jointType)) {
        return jointType.map((process: any, index: number) => ({
          value: selectedJointTypeIds.length > 0 && selectedJointTypeIds[index] ? selectedJointTypeIds[index] : index + 1,
          label: process
        }));
      } else if (typeof jointType === 'string') {
        // If processType is a single string, convert it to an array with one item
        return [{
          value: selectedJointTypeIds.length > 0 && selectedJointTypeIds[0] ? selectedJointTypeIds[0] : 1, 
          label: jointType
        }];
      }

      // If no processType found, try to use the fileName as a fallback
      if (fileName) {
        return [{
          value: selectedJointTypeIds.length > 0 && selectedJointTypeIds[0] ? selectedJointTypeIds[0] : 1,
          label: jointType
        }];
      }
    }
      return [];
    });


  const handleCustomerSignatureChange = (signature: string | null) => {
    setCustomerSignature(signature);
    if (alignmentWeldData) {
      alignmentWeldData.customerRepresentativeId = signature;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  const handleQcSignatureChange = (signature: string | null) => {
    setQcSignature(signature);
    if (alignmentWeldData) {
      alignmentWeldData.qcInspectorId = signature;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  const OnHandleSignature = (comment: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    if (alignmentWeldData) {
      alignmentWeldData.signature = {
        ...alignmentWeldData.signature,
        [comment.type]: {
          signature: comment.signature,
          name: comment.name,
          comment: comment.comment,
          stampImage: comment.stampImage,
          timestamp: new Date().toISOString()
        }
      };
      if (onAlignmentStateChange) {
        onAlignmentStateChange(true);
      }
    }
  };

  // Missing handlers for form controls
  const handleInspectionProcedureChange_visualInspection = (value: string[]) => {
    setSelectedInspectionProcedureIds(value);
    if (alignmentWeldData) {
      if (!alignmentWeldData.details) {
        alignmentWeldData.details = {};
      }
      alignmentWeldData.details.inspectionProcedureIds = value;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

    const handleWeldPositionChange_visualInspection = (value: string[]) => {
    setSelectedWeldPositionIds(value);
    if (alignmentWeldData) {
      if (!alignmentWeldData.details) {
        alignmentWeldData.details = {};
      }
      alignmentWeldData.details.weldPositionIds = value;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  const handleJointTypeChange_visualInspection = (value: string[]) => {
    setSelectedJointTypeIds(value);
    if (alignmentWeldData) {
      if (!alignmentWeldData.details) {
        alignmentWeldData.details = {};
      }
      alignmentWeldData.details.jointTypeIds = value;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  const handleAlignmentDataChange = (id: number, field: keyof AlignmentData, value: number | string) => {
    setDynamicAlignmentData(prev => {
      const updatedData = prev.map(item =>
        item.id === id
          ? { ...item, [field]: value }
          : item
      );
      
      // Update the alignmentWeldData
      if (alignmentWeldData) {
        alignmentWeldData.alignmentReadings = updatedData;
      }

      return updatedData;
    });
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  // Handle dynamic alignment data changes
  const handleDynamicAlignmentDataChange = (id: number, degree: string, value: number) => {
    setDynamicAlignmentData(prev => {
      const updatedData = prev.map(item =>
        item.id === id
          ? {
            ...item,
            readings: {
              ...item.readings,
              [degree]: value
            }
          }
          : item
      );

      // Update the alignmentWeldData
      if (alignmentWeldData) {
        alignmentWeldData.alignmentReadings = updatedData;
      }

      return updatedData;
    });

    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  // Handle location change
  const handleLocationChange = (id: number, value: string) => {
    setDynamicAlignmentData(prev => {
      const updatedData = prev.map(item =>
        item.id === id
          ? { ...item, location: value }
          : item
      );

      // Update the alignmentWeldData
      if (alignmentWeldData) {
        alignmentWeldData.alignmentReadings = updatedData;
      }

      return updatedData;
    });

    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  // Handle note change
  const handleNoteChange = (id: number, value: string) => {
    setDynamicAlignmentData(prev => {
      const updatedData = prev.map(item =>
        item.id === id
          ? { ...item, note: value }
          : item
      );

      // Update the alignmentWeldData
      if (alignmentWeldData) {
        alignmentWeldData.alignmentReadings = updatedData;
      }

      return updatedData;
    });

    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };




  const handleSaveNotes = (value: string) => {
    // Explicitly allow empty string to clear the note
    const noteValue = value === null || value === undefined ? '' : value;
    setNotes(noteValue);
    if (alignmentWeldData) {
      if (!alignmentWeldData.details) {
        alignmentWeldData.details = {};
      }
      alignmentWeldData.details.note = noteValue;
    }

    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };
  
    const handleReadingsNotes = (value: string) => {
      setReadingsNotes(value);
      if (alignmentWeldData) {
        if (!alignmentWeldData.details) {
          alignmentWeldData.details = {};
        }
        alignmentWeldData.details.readingsNote = value;
      }

    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };
  
  const handleVerificationStatementSaveNotes = (value: string) => {
    setVerificationStatementNotes(value);
    if (alignmentWeldData) {
      if (!alignmentWeldData.details) {
        alignmentWeldData.details = {};
      }
      alignmentWeldData.details.verificationStatementNote = value;
      alignmentWeldData.verificationStatement = value;
    }
    if (onAlignmentStateChange) {
      onAlignmentStateChange(true);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-700">Alignment Details</h4>
            </div>
            
            {/* Main Form Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-8 gap-2">


              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Date</Label>
                <div className="flex items-center space-x-2">
                  <AntdDate
                    name="startDate"
                    value={date}
                    onChange={(value: string) => {
                      setDate(value);
                      if (alignmentWeldData) {
                        alignmentWeldData.date = value;
                        if (onAlignmentStateChange) {
                          onAlignmentStateChange(true);
                        }
                      }
                    }}
                    disabled={!isEditMode}
                  />

                </div>
              </div>


              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Inspection Procedure</Label>
                <div className="w-full">
                  <MultiSelect
                    options={inspectionProcedureOptions || []}
                    selected={selectedInspectionProcedureIds}
                    onChange={isEditMode ? handleInspectionProcedureChange_visualInspection : () => {}}
                  />
                </div>
              </div>

              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Weld Position</Label>
                <div className="w-full">
                  <MultiSelect
                    options={weldPositionOptions || []}
                    selected={selectedWeldPositionIds}
                    onChange={isEditMode ? handleWeldPositionChange_visualInspection : () => {}}
                  />
                </div>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Joint Type</Label>
                <div className="w-full">
                  <MultiSelect
                    options={jointTypeOptions || []}
                    selected={selectedJointTypeIds}
                    onChange={isEditMode ? handleJointTypeChange_visualInspection : () => {}}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600"> Overall Length ({getUnitLabel(unit)})</Label>
                                 <Input
                   type="number"
                   className="w-full min-w-[120px] hover:bg-gray-50 focus:bg-white transition-colors duration-200"
                   value={detailedOverallLength ? getDisplayValue(detailedOverallLength) : ''}
                   onChange={(e) => {
                     const inputValue = e.target.value;
                     if (inputValue === '') {
                       setDetailedOverallLength(null);
                       if (alignmentWeldData?.details) {
                         alignmentWeldData.details.lengthEndToEnd = null;
                         if (onAlignmentStateChange) {
                           onAlignmentStateChange(true);
                         }
                       }
                     } else {
                       const newValue = convertInputToInches(parseFloat(inputValue) || 0);
                       setDetailedOverallLength(newValue);
                       if (alignmentWeldData?.details) {
                         alignmentWeldData.details.lengthEndToEnd = newValue;
                         if (onAlignmentStateChange) {
                           onAlignmentStateChange(true);
                         }
                       }
                     }
                   }}
                   placeholder="Enter overall length..."
                   disabled={!isEditMode}
                 />
              </div>
           
        
                <div className="space-y-2 col-span-8">
                  <Label className="text-sm font-medium text-gray-600">Notes</Label>
                <Input
                       value={notes ?? ''}
                       onChange={(e) => handleSaveNotes(e.target.value)}
                       className="w-full min-w-[120px] hover:bg-gray-50 focus:bg-white transition-colors duration-200"
                       placeholder="Enter notes statement..."
                       disabled={!isEditMode}
                     />
                 </div>
               </div>


          </div>
        </CardContent>
      </Card>


      {/* Dynamic Alignment Verification Table */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700">Alignment Verification</h4>
          </div>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center w-32">Location</TableHead>
                  {selectedDegrees.map((degree: any) => (
                    <TableHead key={degree} className="text-center w-32">
                      {degree}°
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {dynamicAlignmentData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-center">
                      {item.location}
                    </TableCell>
                    {selectedDegrees.map((degree: any) => {
                      const degreeKey = `${degree}deg`;
                      return (
                        <TableCell key={degree} className="text-center">
                       <div>
                                                     <Input
                             type="number"
                             value={item?.readings?.[degreeKey] || 0}
                             onChange={(e) => handleDynamicAlignmentDataChange(
                               item.id,
                               degreeKey,
                               parseFloat(e.target.value) || 0
                             )}
                             className="w-full min-w-[120px] hover:bg-gray-50 focus:bg-white transition-colors duration-200 text-center"
                             placeholder="0"
                             disabled={!isEditMode}
                           />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
                                           <div className="space-y-2">
                    <Label className="text-base font-medium">Notes</Label>
                    <textarea
                      value={readingsNotes}
                      onChange={(e) => handleReadingsNotes(e.target.value)}
                      className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                      placeholder="Enter verification statement..."
                      disabled={!isEditMode}
                    />
                  </div>
        </CardContent>
      </Card>

      {/* Verification Statement */}
      <Card>
        <CardContent className="p-6">
                                           <div >
              <Label className="text-base font-medium">Verification Statement:</Label>
              <textarea
                value={verificationStatementNotes}
                onChange={(e) => handleVerificationStatementSaveNotes(e.target.value)}
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-700"
                placeholder="Enter verification statement..."
                disabled={!isEditMode}
              />
            </div>
        </CardContent>
      </Card>

      {/* Signature Section */}
      <Signature
        customerSignature={customerSignature}
        qcSignature={qcSignature}
        handleQcSignatureChange={handleQcSignatureChange}
        handleCustomerSignatureChange={handleCustomerSignatureChange}
        customerRepresentativeList={customerRepresentativeList}
        qcList={qcList}
        onSignatureChange={OnHandleSignature}
        signatureData={alignmentWeldData}
        canSign={canSign}
        canEdit={canEdit}
        isEditMode={isEditMode}
        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
        isQCInspectorUser={isQCInspectorUser}
        isWelderUser={isWelderUser}
        currentUserTeamMemberId={currentUserTeamMemberId}
        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      />


    </div>
  );
}; 